import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Badge,
  Slider,
  Checkbox,
  FormControlLabel,
  FormGroup,
} from '@mui/material';
import {
  Search,
  Add,
  MoreVert,
  Edit,
  Delete,
  Visibility,
  FilterList,
  Work,
  AttachMoney,
  Schedule,
  Build,
  Person,
  Business,
  LocationOn,
  Phone,
  Email,
  ArrowBack,
  ArrowUpward,
  ArrowDownward,
  Warning,
} from '@mui/icons-material';
import axios from 'axios';
import { mockApi } from '../../services/mockData';

interface Customer {
  _id: string;
  companyName: string;
  contactPerson: {
    firstName: string;
    lastName: string;
    title?: string;
  };
  email: string;
  phone: string;
  address?: {
    street: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
  };
  customerType: string;
  status: string;
  totalProjects: number;
  totalValue: number;
  footage: number; // Total footage for this customer
  paymentAmount: number; // Amount customer is paying
  lastContactDate?: string;
  assignedSalesRep?: {
    firstName: string;
    lastName: string;
  };
  notes?: string;
  projects?: Project[];
}

interface Project {
  _id: string;
  name: string;
  description: string;
  status: 'quoted' | 'in_progress' | 'completed' | 'cancelled' | 'on_hold';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  estimatedValue: number;
  actualValue?: number;
  startDate?: string;
  endDate?: string;
  estimatedDuration: number;
  actualDuration?: number;
  assignedTeam: Array<{
    id: string;
    name: string;
    role: string;
  }>;
  location: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

const Customers: React.FC = () => {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [priceInputOpen, setPriceInputOpen] = useState(false);
  const [tempPricePerFoot, setTempPricePerFoot] = useState(27.00);
  const [trackRows, setTrackRows] = useState([{ id: 1, trackType: '', color: '', footage: '' }]);
  const [finalizeData, setFinalizeData] = useState<{
    sidesInstalled: string[];
    lightCount: string;
    controllerBoxes: string;
    transistors: string;
    yardSignPhoto: boolean;
    wifiSetupShown: boolean;
    appControlShown: boolean;
    clientIPAddress: string;
  }>({
    sidesInstalled: [],
    lightCount: '',
    controllerBoxes: '',
    transistors: '',
    yardSignPhoto: false,
    wifiSetupShown: false,
    appControlShown: false,
    clientIPAddress: ''
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editFormData, setEditFormData] = useState({
    companyName: '',
    email: '',
    phone: '',
    address: { street: '', city: '', state: '', zipCode: '', country: 'USA' },
    customerType: '',
    status: '',
    notes: ''
  });
  const [proposalData, setProposalData] = useState({
    measurements: {
      front: 75,
      left: 0,
      back: 0,
      right: 0
    },
    pricing: {
      pricePerFoot: 27.00,
      deposit: 1012.50,
      totalPrice: 2025.00
    },
    projectDetails: {
      name: 'Residential LED Installation',
      type: 'Outdoor Perimeter Lighting',
      description: 'Installation of LED perimeter lighting around residential property including front, back, left, and right sides.',
      duration: '2 days',
      priority: 'Medium',
      weatherDependent: 'Yes'
    }
  });
  const [viewMode, setViewMode] = useState<'list' | 'detail'>('list');
  const [tabValue, setTabValue] = useState(0);
  const [sortField, setSortField] = useState('companyName');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const navigate = useNavigate();

  const fetchCustomers = useCallback(async () => {
    try {
      setLoading(true);
      
      const response = await mockApi.getCustomers({
        search: searchTerm,
        status: statusFilter,
        customerType: typeFilter
      });
      
      setCustomers(response.customers as Customer[]);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch customers');
    } finally {
      setLoading(false);
    }
  }, [searchTerm, statusFilter, typeFilter]);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  // Load proposal data when a customer is selected
  useEffect(() => {
    if (selectedCustomer && tabValue === 1) { // Tab 1 is the Proposal tab
      loadProposalData(selectedCustomer._id);
    }
  }, [selectedCustomer?._id, tabValue]); // Only depend on customer ID, not the entire customer object

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, customer: Customer) => {
    setAnchorEl(event.currentTarget);
    setSelectedCustomer(customer);
  };

  const handleViewDetails = (customer: Customer) => {
    setSelectedCustomer(customer);
    setViewMode('detail');
    setTabValue(0); // Reset to first tab
    // Load proposal data for this customer
    loadProposalData(customer._id);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedCustomer(null);
  };

  const handleView = () => {
    if (selectedCustomer) {
      setViewMode('detail');
      setTabValue(0); // Reset to first tab
      // Load proposal data for this customer
      loadProposalData(selectedCustomer._id);
    }
    handleMenuClose();
  };

  const handleBackToList = () => {
    setViewMode('list');
    setSelectedCustomer(null);
  };

  const handleEdit = () => {
    if (selectedCustomer) {
      setEditFormData({
        companyName: selectedCustomer.companyName,
        email: selectedCustomer.email,
        phone: selectedCustomer.phone,
        address: {
          street: selectedCustomer.address?.street || '',
          city: selectedCustomer.address?.city || '',
          state: selectedCustomer.address?.state || '',
          zipCode: selectedCustomer.address?.zipCode || '',
          country: selectedCustomer.address?.country || 'USA'
        },
        customerType: selectedCustomer.customerType,
        status: selectedCustomer.status,
        notes: selectedCustomer.notes || ''
      });
      setEditDialogOpen(true);
    }
    handleMenuClose();
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const handleEditFormChange = (field: string, value: any) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setEditFormData(prev => ({
        ...prev,
        [parent]: {
          ...(prev[parent as keyof typeof prev] as any),
          [child]: value
        }
      }));
    } else {
      setEditFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleSaveCustomer = async () => {
    if (!selectedCustomer) return;

    try {
      setLoading(true);
      await mockApi.updateCustomer(selectedCustomer._id, editFormData);
      
      // Update the customer in the local state
      setSelectedCustomer(prev => prev ? { ...prev, ...editFormData } : null);
      
      // Refresh the customers list
      await fetchCustomers();
      
      setEditDialogOpen(false);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update customer');
    } finally {
      setLoading(false);
    }
  };

  const handleProposalChange = (field: string, value: any) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setProposalData(prev => ({
        ...prev,
        [parent]: {
          ...(prev[parent as keyof typeof prev] as any),
          [child]: value
        }
      }));
    } else {
      setProposalData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const calculateTotalLinearFeet = () => {
    try {
      const measurements = proposalData?.measurements || {};
      return (measurements.front || 0) + 
             (measurements.left || 0) + 
             (measurements.back || 0) + 
             (measurements.right || 0);
    } catch (error) {
      console.error('Error calculating total linear feet:', error);
      return 0;
    }
  };

  const calculateTotalPrice = () => {
    try {
      const totalFeet = calculateTotalLinearFeet();
      const pricePerFoot = proposalData?.pricing?.pricePerFoot || 0;
      return totalFeet * pricePerFoot;
    } catch (error) {
      console.error('Error calculating total price:', error);
      return 0;
    }
  };

  const loadProposalData = async (customerId: string) => {
    // Prevent infinite loops by checking if we're already loading
    if (loading) return;
    
    try {
      setLoading(true);
      
      // Try to load from backend first
      const response = await axios.get(`/api/proposals/draft/${customerId}`);
      if (response.data.success) {
        const proposalData = response.data.data;
        setProposalData(proposalData);
        
        // Update customer data with proposal calculations
        if (proposalData.measurements && proposalData.pricing) {
          const totalFeet = proposalData.measurements.totalFeet || 
            (proposalData.measurements.front + proposalData.measurements.left + 
             proposalData.measurements.back + proposalData.measurements.right);
          const totalPrice = proposalData.pricing.totalPrice || 
            (totalFeet * proposalData.pricing.pricePerFoot);
          
          // Only update if values have changed to prevent infinite loops
          const currentCustomer = customers.find(c => c._id === customerId);
          if (currentCustomer && 
              (currentCustomer.footage !== totalFeet || 
               currentCustomer.paymentAmount !== totalPrice)) {
            
            // Update customer data in backend
            try {
              await mockApi.updateCustomerFromProposal({
                customerId: customerId,
                footage: totalFeet,
                paymentAmount: totalPrice,
                totalValue: totalPrice
              });
              console.log('Customer data synced to backend from proposal'); // Debug log
            } catch (updateError) {
              console.warn('Failed to sync customer data to backend:', updateError);
            }
            
            // Update the customer in the customers list
            setCustomers(prev => 
              prev.map(c => c._id === customerId ? {
                ...c,
                footage: totalFeet,
                paymentAmount: totalPrice,
                totalValue: totalPrice
              } : c)
            );
            
            // Update selectedCustomer if it's the same customer
            if (selectedCustomer && selectedCustomer._id === customerId) {
              setSelectedCustomer(prev => prev ? {
                ...prev,
                footage: totalFeet,
                paymentAmount: totalPrice,
                totalValue: totalPrice
              } : null);
            }
          }
        }
        return;
      }
    } catch (error) {
      console.warn('Backend proposal load failed, trying localStorage:', error);
    }
    
    // If backend fails, try localStorage
    try {
      const existingDrafts = JSON.parse(localStorage.getItem('proposalDrafts') || '[]');
      const draft = existingDrafts.find((d: any) => d.customerId === customerId);
      if (draft) {
        setProposalData(draft);
        
        // Update customer data with proposal calculations from localStorage
        if (draft.measurements && draft.pricing) {
          const totalFeet = draft.measurements.totalFeet || 
            (draft.measurements.front + draft.measurements.left + 
             draft.measurements.back + draft.measurements.right);
          const totalPrice = draft.pricing.totalPrice || 
            (totalFeet * draft.pricing.pricePerFoot);
          
          // Only update if values have changed to prevent infinite loops
          const currentCustomer = customers.find(c => c._id === customerId);
          if (currentCustomer && 
              (currentCustomer.footage !== totalFeet || 
               currentCustomer.paymentAmount !== totalPrice)) {
            
            // Update customer data in backend
            try {
              await mockApi.updateCustomerFromProposal({
                customerId: customerId,
                footage: totalFeet,
                paymentAmount: totalPrice,
                totalValue: totalPrice
              });
              console.log('Customer data synced to backend from localStorage proposal'); // Debug log
            } catch (updateError) {
              console.warn('Failed to sync customer data to backend from localStorage:', updateError);
            }
            
            // Update the customer in the customers list
            setCustomers(prev => 
              prev.map(c => c._id === customerId ? {
                ...c,
                footage: totalFeet,
                paymentAmount: totalPrice,
                totalValue: totalPrice
              } : c)
            );
            
            // Update selectedCustomer if it's the same customer
            if (selectedCustomer && selectedCustomer._id === customerId) {
              setSelectedCustomer(prev => prev ? {
                ...prev,
                footage: totalFeet,
                paymentAmount: totalPrice,
                totalValue: totalPrice
              } : null);
            }
          }
        }
      }
    } catch (localError) {
      console.error('Error loading proposal data from localStorage:', localError);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveDraft = async () => {
    console.log('handleSaveDraft called'); // Debug log
    
    if (!selectedCustomer) {
      console.error('No customer selected');
      setError('No customer selected');
      return;
    }

    try {
      console.log('Starting save draft process'); // Debug log
      setLoading(true);
      setError(''); // Clear any existing errors
      
      // Validate proposalData exists
      if (!proposalData) {
        console.error('proposalData is null or undefined');
        setError('Proposal data is not available');
        return;
      }
      
      const totalFeet = calculateTotalLinearFeet();
      const totalPrice = calculateTotalPrice();
      
      console.log('Calculated values:', { totalFeet, totalPrice }); // Debug log
      
      const draftData = {
        ...proposalData,
        measurements: {
          ...(proposalData.measurements || {}),
          totalFeet
        },
        pricing: {
          ...(proposalData.pricing || {}),
          totalPrice
        },
        customerId: selectedCustomer._id,
        status: 'draft',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      console.log('Saving proposal draft:', draftData); // Debug log

      // Always save to localStorage first as backup
      try {
        const existingDrafts = JSON.parse(localStorage.getItem('proposalDrafts') || '[]');
        const draftIndex = existingDrafts.findIndex((draft: any) => draft.customerId === selectedCustomer._id);
        
        if (draftIndex >= 0) {
          existingDrafts[draftIndex] = draftData;
        } else {
          existingDrafts.push(draftData);
        }
        
        localStorage.setItem('proposalDrafts', JSON.stringify(existingDrafts));
        console.log('Saved to localStorage successfully'); // Debug log
      } catch (localError) {
        console.error('Error saving to localStorage:', localError);
        setError('Failed to save to local storage');
        return;
      }

      // Try to save to backend API
      try {
        const response = await mockApi.saveProposalDraft(draftData);
        console.log('API response:', response.data); // Debug log
        
        if (!response.data.success) {
          console.warn('API returned success: false, but data saved to localStorage');
        }
        
        // Update customer data in backend
        try {
          await axios.post('/api/proposals/update-customer', {
            customerId: selectedCustomer._id,
            footage: totalFeet,
            paymentAmount: totalPrice,
            totalValue: totalPrice
          });
          console.log('Customer data updated in backend'); // Debug log
        } catch (updateError) {
          console.warn('Failed to update customer data in backend:', updateError);
        }
      } catch (apiError) {
        console.warn('API save failed, but data saved to localStorage:', apiError);
      }
      
      // Update customer data with proposal calculations
      const updatedCustomer = {
        ...selectedCustomer,
        footage: totalFeet,
        paymentAmount: totalPrice,
        totalValue: totalPrice
      };

      console.log('Updating customer data:', updatedCustomer); // Debug log

      // Update the customer in the customers list
      setCustomers(prev => {
        try {
          return prev.map(c => c._id === selectedCustomer._id ? updatedCustomer : c);
        } catch (error) {
          console.error('Error updating customers list:', error);
          return prev;
        }
      });

      // Update selectedCustomer
      setSelectedCustomer(updatedCustomer);
      
      // Show success message
      setSuccessMessage('Proposal draft saved successfully!');
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(''), 3000);
      
      console.log('Save draft process completed successfully'); // Debug log
      
    } catch (err: any) {
      console.error('Error saving proposal draft:', err); // Debug log
      setError('Failed to save proposal draft: ' + (err.response?.data?.message || err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handlePriceInputOpen = () => {
    setTempPricePerFoot(proposalData.pricing.pricePerFoot);
    setPriceInputOpen(true);
  };

  const handlePriceInputSave = () => {
    if (tempPricePerFoot >= 10 && tempPricePerFoot <= 50) {
      handleProposalChange('pricing.pricePerFoot', tempPricePerFoot);
      setPriceInputOpen(false);
    }
  };

  const handlePriceInputCancel = () => {
    setPriceInputOpen(false);
  };

  const handleAddTrackRow = () => {
    const newRow = {
      id: trackRows.length + 1,
      trackType: '',
      color: '',
      footage: ''
    };
    setTrackRows([...trackRows, newRow]);
  };

  const handleTrackRowChange = (rowId: number, field: string, value: string) => {
    setTrackRows(trackRows.map(row => 
      row.id === rowId ? { ...row, [field]: value } : row
    ));
  };

  const handleDeleteTrackRow = (rowId: number) => {
    if (trackRows.length > 1) {
      setTrackRows(trackRows.filter(row => row.id !== rowId));
    }
  };

  const handleFinalizeChange = (field: string, value: any) => {
    setFinalizeData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSideToggle = (side: string) => {
    setFinalizeData(prev => ({
      ...prev,
      sidesInstalled: prev.sidesInstalled.includes(side)
        ? prev.sidesInstalled.filter(s => s !== side)
        : [...prev.sidesInstalled, side]
    }));
  };

  const handleYardSignPhotoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Here you would typically upload the file to a server
      // For now, we'll just mark it as uploaded
      setFinalizeData(prev => ({
        ...prev,
        yardSignPhoto: true
      }));
    }
  };

  const handleDeleteConfirm = async () => {
    if (selectedCustomer) {
      try {
        await mockApi.deleteCustomer(selectedCustomer._id);
        setCustomers(customers.filter(c => c._id !== selectedCustomer._id));
        setDeleteDialogOpen(false);
      } catch (err: any) {
        setError(err.response?.data?.message || 'Failed to delete customer');
      }
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Created': return 'default';
      case 'Proposal': return 'warning';
      case 'Signed/Paid': return 'info';
      case 'Sold': return 'success';
      case 'Scheduled': return 'primary';
      case 'Installed': return 'success';
      case 'Canceled': return 'error';
      default: return 'default';
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'commercial': return 'primary';
      case 'residential': return 'secondary';
      case 'industrial': return 'info';
      case 'municipal': return 'warning';
      default: return 'default';
    }
  };

  const getProjectStatusColor = (status: string) => {
    switch (status) {
      case 'quoted': return 'info';
      case 'in_progress': return 'warning';
      case 'completed': return 'success';
      case 'cancelled': return 'error';
      case 'on_hold': return 'default';
      default: return 'default';
    }
  };

  const getProjectPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'error';
      case 'high': return 'warning';
      case 'medium': return 'info';
      case 'low': return 'success';
      default: return 'default';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortedCustomers = () => {
    return [...customers].sort((a, b) => {
      let aValue: any = a[sortField as keyof Customer];
      let bValue: any = b[sortField as keyof Customer];



      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (viewMode === 'detail' && selectedCustomer) {
    return (
      <Box sx={{ backgroundColor: '#000000', minHeight: '100vh', p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Box display="flex" alignItems="center" gap={2}>
            <Button
              variant="outlined"
              onClick={handleBackToList}
              startIcon={<ArrowBack />}
            >
              Back to List
            </Button>
            <Typography 
              variant="h4"
              sx={{
                color: '#D4AF37',
                fontWeight: 700,
              }}
            >
              {selectedCustomer.companyName}
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<Edit />}
            onClick={handleEdit}
          >
            Edit Customer
          </Button>
        </Box>

        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)} sx={{ mb: 3 }}>
          <Tab label="Customer Details" />
          <Tab label="Proposal" />
          <Tab label="Sign/Pay" />
          <Tab label="Photos" />
          <Tab label="Track" />
          <Tab label="STOCKTON" />
          <Tab label="Finalize" />
          <Tab label="Contact History" />
        </Tabs>

        {tabValue === 0 && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ color: '#D4AF37' }}>
                    Company Information
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <Business />
                    <Typography variant="body1">{selectedCustomer.companyName}</Typography>
                  </Box>
                  <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <Email />
                    <Typography variant="body2">{selectedCustomer.email}</Typography>
                  </Box>
                  <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <Phone />
                    <Typography variant="body2">{selectedCustomer.phone}</Typography>
                  </Box>
                  {selectedCustomer.address && (
                    <Box display="flex" alignItems="flex-start" gap={1} mb={2}>
                      <LocationOn />
                      <Typography variant="body2">
                        {selectedCustomer.address.street}<br />
                        {selectedCustomer.address.city}, {selectedCustomer.address.state} {selectedCustomer.address.zipCode}
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ color: '#D4AF37' }}>
                    Business Information
                  </Typography>
                  <Box display="flex" gap={1} mb={2}>
                    <Chip
                      label={selectedCustomer.status}
                      color={getStatusColor(selectedCustomer.status) as any}
                      size="small"
                    />
                    <Chip
                      label={selectedCustomer.customerType}
                      color={getTypeColor(selectedCustomer.customerType) as any}
                      size="small"
                    />
                  </Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Total Projects: {selectedCustomer.totalProjects}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Total Value: {formatCurrency(selectedCustomer.totalValue)}
                  </Typography>
                  {selectedCustomer.assignedSalesRep && (
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Sales Rep: {selectedCustomer.assignedSalesRep.firstName} {selectedCustomer.assignedSalesRep.lastName}
                    </Typography>
                  )}
                  {selectedCustomer.lastContactDate && (
                    <Typography variant="body2" color="text.secondary">
                      Last Contact: {formatDate(selectedCustomer.lastContactDate)}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {tabValue === 1 && (
          <Box>
            <Typography variant="h6" sx={{ color: '#D4AF37', mb: 3 }}>
              Proposal
            </Typography>
            
            {successMessage && (
              <Alert severity="success" sx={{ mb: 2, backgroundColor: '#1B5E20', color: '#FFFFFF' }}>
                {successMessage}
              </Alert>
            )}
            
            {error && (
              <Alert severity="error" sx={{ mb: 2, backgroundColor: '#2D1B1B', color: '#FF6B6B' }}>
                {error}
              </Alert>
            )}
            
            <Grid container spacing={3}>
              {/* Linear Measurements */}
              <Grid item xs={12} md={6}>
                <Card sx={{ backgroundColor: '#2C2C2C', border: '1px solid #333333' }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ color: '#D4AF37', mb: 2 }}>
                      Linear Measurements
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          label="Front (ft)"
                          type="number"
                          value={proposalData.measurements.front}
                          onChange={(e) => handleProposalChange('measurements.front', Number(e.target.value))}
                          InputProps={{
                            endAdornment: (
                              <Box sx={{ display: 'flex', flexDirection: 'column', ml: 1 }}>
                                <IconButton 
                                  size="small" 
                                  sx={{ p: 0.5 }}
                                  onClick={() => handleProposalChange('measurements.front', proposalData.measurements.front + 1)}
                                >
                                  <ArrowUpward fontSize="small" />
                                </IconButton>
                                <IconButton 
                                  size="small" 
                                  sx={{ p: 0.5 }}
                                  onClick={() => handleProposalChange('measurements.front', Math.max(0, proposalData.measurements.front - 1))}
                                >
                                  <ArrowDownward fontSize="small" />
                                </IconButton>
                              </Box>
                            )
                          }}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          label="Left (ft)"
                          type="number"
                          value={proposalData.measurements.left}
                          onChange={(e) => handleProposalChange('measurements.left', Number(e.target.value))}
                          InputProps={{
                            endAdornment: (
                              <Box sx={{ display: 'flex', flexDirection: 'column', ml: 1 }}>
                                <IconButton 
                                  size="small" 
                                  sx={{ p: 0.5 }}
                                  onClick={() => handleProposalChange('measurements.left', proposalData.measurements.left + 1)}
                                >
                                  <ArrowUpward fontSize="small" />
                                </IconButton>
                                <IconButton 
                                  size="small" 
                                  sx={{ p: 0.5 }}
                                  onClick={() => handleProposalChange('measurements.left', Math.max(0, proposalData.measurements.left - 1))}
                                >
                                  <ArrowDownward fontSize="small" />
                                </IconButton>
                              </Box>
                            )
                          }}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          label="Back (ft)"
                          type="number"
                          value={proposalData.measurements.back}
                          onChange={(e) => handleProposalChange('measurements.back', Number(e.target.value))}
                          InputProps={{
                            endAdornment: (
                              <Box sx={{ display: 'flex', flexDirection: 'column', ml: 1 }}>
                                <IconButton 
                                  size="small" 
                                  sx={{ p: 0.5 }}
                                  onClick={() => handleProposalChange('measurements.back', proposalData.measurements.back + 1)}
                                >
                                  <ArrowUpward fontSize="small" />
                                </IconButton>
                                <IconButton 
                                  size="small" 
                                  sx={{ p: 0.5 }}
                                  onClick={() => handleProposalChange('measurements.back', Math.max(0, proposalData.measurements.back - 1))}
                                >
                                  <ArrowDownward fontSize="small" />
                                </IconButton>
                              </Box>
                            )
                          }}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          label="Right (ft)"
                          type="number"
                          value={proposalData.measurements.right}
                          onChange={(e) => handleProposalChange('measurements.right', Number(e.target.value))}
                          InputProps={{
                            endAdornment: (
                              <Box sx={{ display: 'flex', flexDirection: 'column', ml: 1 }}>
                                <IconButton 
                                  size="small" 
                                  sx={{ p: 0.5 }}
                                  onClick={() => handleProposalChange('measurements.right', proposalData.measurements.right + 1)}
                                >
                                  <ArrowUpward fontSize="small" />
                                </IconButton>
                                <IconButton 
                                  size="small" 
                                  sx={{ p: 0.5 }}
                                  onClick={() => handleProposalChange('measurements.right', Math.max(0, proposalData.measurements.right - 1))}
                                >
                                  <ArrowDownward fontSize="small" />
                                </IconButton>
                              </Box>
                            )
                          }}
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* Pricing Summary */}
              <Grid item xs={12} md={6}>
                <Card sx={{ backgroundColor: '#2C2C2C', border: '1px solid #333333' }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ color: '#D4AF37', mb: 2 }}>
                      Pricing Summary
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Box sx={{ p: 2, backgroundColor: '#1A1A1A', borderRadius: 1, border: '1px solid #333333' }}>
                          <Typography variant="body2" color="text.secondary">
                            Total linear feet
                          </Typography>
                          <Typography variant="h6" sx={{ color: '#FFFFFF' }}>
                            {calculateTotalLinearFeet()}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12}>
                        <Box sx={{ p: 2, backgroundColor: '#1A1A1A', borderRadius: 1, border: '1px solid #333333' }}>
                          <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                              Price per foot
                            </Typography>
                            <IconButton 
                              size="small" 
                              onClick={handlePriceInputOpen}
                              sx={{ color: '#D4AF37', p: 0.5 }}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                          </Box>
                          <Typography variant="h6" sx={{ color: '#FFFFFF', mb: 2 }}>
                            ${proposalData.pricing.pricePerFoot.toFixed(2)}
                          </Typography>
                          <Slider
                            value={proposalData.pricing.pricePerFoot}
                            onChange={(e, value) => handleProposalChange('pricing.pricePerFoot', value)}
                            min={10}
                            max={50}
                            step={0.05}
                            sx={{
                              color: '#D4AF37',
                              '& .MuiSlider-thumb': {
                                backgroundColor: '#D4AF37',
                                '&:hover': {
                                  backgroundColor: '#B8941F',
                                },
                              },
                              '& .MuiSlider-track': {
                                backgroundColor: '#D4AF37',
                              },
                              '& .MuiSlider-rail': {
                                backgroundColor: '#555555',
                              },
                            }}
                          />
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                            <Typography variant="caption" sx={{ color: '#AAAAAA' }}>
                              $10.00
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#AAAAAA' }}>
                              $50.00
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                      <Grid item xs={12}>
                        <Box sx={{ p: 2, backgroundColor: '#1A1A1A', borderRadius: 1, border: '1px solid #333333' }}>
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="body2" color="text.secondary">
                              Deposit
                            </Typography>
                            <IconButton size="small" sx={{ color: '#D4AF37' }}>
                              <Edit fontSize="small" />
                            </IconButton>
                          </Box>
                          <Typography variant="h6" sx={{ color: '#FFFFFF' }}>
                            ${proposalData.pricing.deposit.toFixed(2)}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12}>
                        <Box sx={{ p: 2, backgroundColor: '#1A1A1A', borderRadius: 1, border: '1px solid #333333' }}>
                          <Typography variant="body2" color="text.secondary">
                            Total price
                          </Typography>
                          <Typography variant="h6" sx={{ color: '#D4AF37' }}>
                            ${calculateTotalPrice().toFixed(2)}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12}>
                        <Box sx={{ p: 2, backgroundColor: '#1A1A1A', borderRadius: 1, border: '1px solid #333333' }}>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            Priority
                          </Typography>
                          <FormControl fullWidth size="small">
                            <Select
                              value={proposalData.projectDetails.priority}
                              onChange={(e) => handleProposalChange('projectDetails.priority', e.target.value)}
                              displayEmpty
                              sx={{
                                color: '#FFFFFF',
                                '& .MuiOutlinedInput-notchedOutline': {
                                  borderColor: '#555555',
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                  borderColor: '#D4AF37',
                                },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                  borderColor: '#D4AF37',
                                },
                                '& .MuiSvgIcon-root': {
                                  color: '#D4AF37',
                                },
                              }}
                            >
                              <MenuItem value="Low">Low</MenuItem>
                              <MenuItem value="Medium">Medium</MenuItem>
                              <MenuItem value="High">High</MenuItem>
                              <MenuItem value="Escalated">Escalated</MenuItem>
                            </Select>
                          </FormControl>
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>


              {/* Action Buttons */}
              <Grid item xs={12}>
                <Box display="flex" gap={2} justifyContent="flex-end">
                  <Button
                    variant="outlined"
                    onClick={handleSaveDraft}
                    disabled={loading}
                    sx={{ color: '#FFFFFF', borderColor: '#D4AF37' }}
                  >
                    {loading ? 'Saving...' : 'Save Draft'}
                  </Button>
                  <Button
                    variant="contained"
                    sx={{ backgroundColor: '#D4AF37', color: '#000000' }}
                  >
                    Generate Proposal
                  </Button>
                  <Button
                    variant="contained"
                    sx={{ backgroundColor: '#D4AF37', color: '#000000' }}
                  >
                    Send to Customer
                  </Button>
                </Box>
              </Grid>
            </Grid>

            {/* Price Input Dialog */}
            <Dialog open={priceInputOpen} onClose={handlePriceInputCancel} maxWidth="sm" fullWidth>
              <DialogTitle sx={{ color: '#D4AF37' }}>
                Edit Price Per Foot
              </DialogTitle>
              <DialogContent dividers>
                <TextField
                  fullWidth
                  label="Price per foot ($)"
                  type="number"
                  value={tempPricePerFoot}
                  onChange={(e) => setTempPricePerFoot(Number(e.target.value))}
                  inputProps={{ 
                    min: 10, 
                    max: 50, 
                    step: 0.05 
                  }}
                  margin="normal"
                  helperText="Enter a value between $10.00 and $50.00"
                />
              </DialogContent>
              <DialogActions>
                <Button onClick={handlePriceInputCancel} sx={{ color: '#FFFFFF' }}>
                  Cancel
                </Button>
                <Button 
                  onClick={handlePriceInputSave}
                  variant="contained"
                  sx={{ backgroundColor: '#D4AF37', color: '#000000' }}
                >
                  Save
                </Button>
              </DialogActions>
            </Dialog>
          </Box>
        )}

        {tabValue === 2 && (
          <Box>
            <Typography variant="h6" sx={{ color: '#D4AF37', mb: 3 }}>
              Sign/Pay
            </Typography>
            
            <Tabs value={tabValue === 2 ? 0 : 1} sx={{ mb: 3 }}>
              <Tab label="Completed Documents" />
              <Tab label="Deposit" />
            </Tabs>

            {/* Completed Documents Tab */}
            <Box sx={{ display: tabValue === 2 ? 'block' : 'none' }}>
              <Card sx={{ backgroundColor: '#2C2C2C', border: '1px solid #333333' }}>
                <CardContent>
                  <Typography variant="h6" sx={{ color: '#D4AF37', mb: 2 }}>
                    📄 Completed Documents
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Documents will be automatically imported from the other website once the customer completes the signing process.
                  </Typography>
                  
                  <Box sx={{ 
                    border: '2px dashed #555555', 
                    borderRadius: 2, 
                    p: 4, 
                    textAlign: 'center',
                    backgroundColor: '#1A1A1A'
                  }}>
                    <Typography variant="h6" sx={{ color: '#AAAAAA', mb: 1 }}>
                      📋 No Documents Yet
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Waiting for customer to complete document signing...
                    </Typography>
                  </Box>

                  <Box sx={{ mt: 3 }}>
                    <Typography variant="subtitle2" sx={{ color: '#D4AF37', mb: 1 }}>
                      Expected Documents:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      <Chip label="Contract Agreement" variant="outlined" sx={{ color: '#AAAAAA', borderColor: '#555555' }} />
                      <Chip label="Payment Terms" variant="outlined" sx={{ color: '#AAAAAA', borderColor: '#555555' }} />
                      <Chip label="Work Authorization" variant="outlined" sx={{ color: '#AAAAAA', borderColor: '#555555' }} />
                      <Chip label="Insurance Forms" variant="outlined" sx={{ color: '#AAAAAA', borderColor: '#555555' }} />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Box>

            {/* Deposit Tab */}
            <Box sx={{ display: tabValue === 2 ? 'block' : 'none' }}>
              <Card sx={{ backgroundColor: '#2C2C2C', border: '1px solid #333333' }}>
                <CardContent>
                  <Typography variant="h6" sx={{ color: '#D4AF37', mb: 2 }}>
                    💰 Deposit Information
                  </Typography>
                  
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <Box sx={{ p: 2, backgroundColor: '#1A1A1A', borderRadius: 1, border: '1px solid #333333' }}>
                        <Typography variant="body2" color="text.secondary">
                          Total Project Value
                        </Typography>
                        <Typography variant="h6" sx={{ color: '#FFFFFF' }}>
                          ${calculateTotalPrice().toFixed(2)}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Box sx={{ p: 2, backgroundColor: '#1A1A1A', borderRadius: 1, border: '1px solid #333333' }}>
                        <Typography variant="body2" color="text.secondary">
                          Deposit Required
                        </Typography>
                        <Typography variant="h6" sx={{ color: '#D4AF37' }}>
                          ${proposalData.pricing.deposit.toFixed(2)}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Box sx={{ p: 2, backgroundColor: '#1A1A1A', borderRadius: 1, border: '1px solid #333333' }}>
                        <Typography variant="body2" color="text.secondary">
                          Amount Paid
                        </Typography>
                        <Typography variant="h6" sx={{ color: '#4CAF50' }}>
                          $0.00
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Box sx={{ p: 2, backgroundColor: '#1A1A1A', borderRadius: 1, border: '1px solid #333333' }}>
                        <Typography variant="body2" color="text.secondary">
                          Remaining Balance
                        </Typography>
                        <Typography variant="h6" sx={{ color: '#FF6B6B' }}>
                          ${proposalData.pricing.deposit.toFixed(2)}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>

                  <Box sx={{ mt: 3, p: 2, backgroundColor: '#1A1A1A', borderRadius: 1, border: '1px solid #333333' }}>
                    <Typography variant="subtitle2" sx={{ color: '#D4AF37', mb: 1 }}>
                      Payment Status
                    </Typography>
                    <Box display="flex" alignItems="center" gap={2}>
                      <Chip 
                        label="Pending Payment" 
                        color="warning" 
                        icon={<Warning />}
                      />
                      <Typography variant="body2" color="text.secondary">
                        Waiting for customer to complete deposit payment
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ mt: 3 }}>
                    <Typography variant="subtitle2" sx={{ color: '#D4AF37', mb: 2 }}>
                      Payment Methods Accepted:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      <Chip label="Credit Card" variant="outlined" sx={{ color: '#AAAAAA', borderColor: '#555555' }} />
                      <Chip label="Bank Transfer" variant="outlined" sx={{ color: '#AAAAAA', borderColor: '#555555' }} />
                      <Chip label="Check" variant="outlined" sx={{ color: '#AAAAAA', borderColor: '#555555' }} />
                      <Chip label="Cash" variant="outlined" sx={{ color: '#AAAAAA', borderColor: '#555555' }} />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Box>
          </Box>
        )}

        {tabValue === 3 && (
          <Box>
            <Typography variant="h6" sx={{ color: '#D4AF37', mb: 3 }}>
              Photos
            </Typography>
            
            <Grid container spacing={3}>
              {/* Front of Home */}
              <Grid item xs={12} md={6}>
                <Card sx={{ backgroundColor: '#2C2C2C', border: '1px solid #333333' }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ color: '#D4AF37', mb: 2 }}>
                      🏠 Front of Home
                    </Typography>
                    <Box sx={{ 
                      border: '2px dashed #555555', 
                      borderRadius: 2, 
                      p: 3, 
                      textAlign: 'center',
                      backgroundColor: '#1A1A1A',
                      minHeight: 150,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center'
                    }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        📸 Upload Front View
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Click to upload or drag & drop
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* Under the Eave */}
              <Grid item xs={12} md={6}>
                <Card sx={{ backgroundColor: '#2C2C2C', border: '1px solid #333333' }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ color: '#D4AF37', mb: 2 }}>
                      🏗️ Under the Eave
                    </Typography>
                    <Box sx={{ 
                      border: '2px dashed #555555', 
                      borderRadius: 2, 
                      p: 3, 
                      textAlign: 'center',
                      backgroundColor: '#1A1A1A',
                      minHeight: 150,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center'
                    }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        📸 Upload Eave View
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Click to upload or drag & drop
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* Color Reference */}
              <Grid item xs={12} md={6}>
                <Card sx={{ backgroundColor: '#2C2C2C', border: '1px solid #333333' }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ color: '#D4AF37', mb: 2 }}>
                      🎨 Color Reference
                    </Typography>
                    <Box sx={{ 
                      border: '2px dashed #555555', 
                      borderRadius: 2, 
                      p: 3, 
                      textAlign: 'center',
                      backgroundColor: '#1A1A1A',
                      minHeight: 150,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center'
                    }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        📸 Upload Color Sample
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Click to upload or drag & drop
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* Light Placement */}
              <Grid item xs={12} md={6}>
                <Card sx={{ backgroundColor: '#2C2C2C', border: '1px solid #333333' }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ color: '#D4AF37', mb: 2 }}>
                      💡 Light Placement (4-5 Photos)
                    </Typography>
                    <Grid container spacing={1}>
                      {[1, 2, 3, 4, 5].map((num) => (
                        <Grid item xs={6} sm={4} key={num}>
                          <Box sx={{ 
                            border: '2px dashed #555555', 
                            borderRadius: 1, 
                            p: 2, 
                            textAlign: 'center',
                            backgroundColor: '#1A1A1A',
                            minHeight: 80,
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'center'
                          }}>
                            <Typography variant="caption" color="text.secondary">
                              📸 Photo {num}
                            </Typography>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* Project Description Video */}
              <Grid item xs={12}>
                <Card sx={{ backgroundColor: '#2C2C2C', border: '1px solid #333333' }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ color: '#D4AF37', mb: 2 }}>
                      🎥 Project Description Video
                    </Typography>
                    <Box sx={{ 
                      border: '2px dashed #555555', 
                      borderRadius: 2, 
                      p: 4, 
                      textAlign: 'center',
                      backgroundColor: '#1A1A1A',
                      minHeight: 200,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center'
                    }}>
                      <Typography variant="h6" sx={{ color: '#AAAAAA', mb: 1 }}>
                        🎬 Upload Project Video
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Upload a video describing the project details and requirements
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Supported formats: MP4, MOV, AVI (Max 100MB)
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        )}

        {tabValue === 4 && (
          <Box>
            <Typography variant="h6" sx={{ color: '#D4AF37', mb: 3 }}>
              Track
            </Typography>
            
            {trackRows.map((row, index) => (
              <Grid container spacing={3} key={row.id} sx={{ mb: 3 }}>
                {/* Track Type */}
                <Grid item xs={12} md={3}>
                  <Card sx={{ backgroundColor: '#2C2C2C', border: '1px solid #333333' }}>
                    <CardContent>
                      <Typography variant="h6" sx={{ color: '#D4AF37', mb: 2 }}>
                        Track Type {trackRows.length > 1 ? `#${index + 1}` : ''}
                      </Typography>
                      <FormControl fullWidth>
                        <InputLabel sx={{ color: '#AAAAAA' }}>Select Track Type</InputLabel>
                        <Select
                          value={row.trackType}
                          onChange={(e) => handleTrackRowChange(row.id, 'trackType', e.target.value)}
                          sx={{
                            color: '#FFFFFF',
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#555555',
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#D4AF37',
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#D4AF37',
                            },
                            '& .MuiSvgIcon-root': {
                              color: '#D4AF37',
                            },
                          }}
                        >
                          <MenuItem value="track-with-flaps">Track with Flaps</MenuItem>
                          <MenuItem value="u-type">U Type</MenuItem>
                          <MenuItem value="back-track">Back Track</MenuItem>
                        </Select>
                      </FormControl>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Track Colors */}
                <Grid item xs={12} md={3}>
                  <Card sx={{ backgroundColor: '#2C2C2C', border: '1px solid #333333' }}>
                    <CardContent>
                      <Typography variant="h6" sx={{ color: '#D4AF37', mb: 2 }}>
                        Track Colors {trackRows.length > 1 ? `#${index + 1}` : ''}
                      </Typography>
                      <FormControl fullWidth>
                        <InputLabel sx={{ color: '#AAAAAA' }}>Select Color</InputLabel>
                        <Select
                          value={row.color}
                          onChange={(e) => handleTrackRowChange(row.id, 'color', e.target.value)}
                          sx={{
                            color: '#FFFFFF',
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#555555',
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#D4AF37',
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#D4AF37',
                            },
                            '& .MuiSvgIcon-root': {
                              color: '#D4AF37',
                            },
                          }}
                        >
                          <MenuItem value="white">White</MenuItem>
                          <MenuItem value="black">Black</MenuItem>
                          <MenuItem value="dark-bronze">Dark Bronze</MenuItem>
                          <MenuItem value="musket-brown">Musket Brown</MenuItem>
                          <MenuItem value="terra-bronze">Terra Bronze</MenuItem>
                          <MenuItem value="pebbleston-clay">Pebbleston Clay</MenuItem>
                          <MenuItem value="wicker">Wicker</MenuItem>
                          <MenuItem value="desert-sand">Desert Sand</MenuItem>
                          <MenuItem value="classic-cream">Classic Cream</MenuItem>
                          <MenuItem value="charcoal-gray">Charcoal Gray</MenuItem>
                          <MenuItem value="almond">Almond</MenuItem>
                          <MenuItem value="victorian-gray">Victorian Gray</MenuItem>
                          <MenuItem value="cameo">Cameo</MenuItem>
                          <MenuItem value="linen">Linen</MenuItem>
                          <MenuItem value="sandtone">Sandtone</MenuItem>
                          <MenuItem value="royal-brown">Royal Brown</MenuItem>
                          <MenuItem value="harbor-grey">Harbor Grey</MenuItem>
                        </Select>
                      </FormControl>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Footage Line */}
                <Grid item xs={12} md={3}>
                  <Card sx={{ backgroundColor: '#2C2C2C', border: '1px solid #333333' }}>
                    <CardContent>
                      <Typography variant="h6" sx={{ color: '#D4AF37', mb: 2 }}>
                        Footage Line {trackRows.length > 1 ? `#${index + 1}` : ''}
                      </Typography>
                      <TextField
                        fullWidth
                        label="Linear Feet"
                        type="number"
                        value={row.footage}
                        onChange={(e) => handleTrackRowChange(row.id, 'footage', e.target.value)}
                        placeholder="Enter footage"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            color: '#FFFFFF',
                            '& fieldset': {
                              borderColor: '#555555',
                            },
                            '&:hover fieldset': {
                              borderColor: '#D4AF37',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#D4AF37',
                            },
                          },
                          '& .MuiInputLabel-root': {
                            color: '#AAAAAA',
                            '&.Mui-focused': {
                              color: '#D4AF37',
                            },
                          },
                        }}
                      />
                    </CardContent>
                  </Card>
                </Grid>

                {/* Delete Row Button */}
                <Grid item xs={12} md={3}>
                  <Card sx={{ backgroundColor: '#2C2C2C', border: '1px solid #333333' }}>
                    <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 120 }}>
                      {trackRows.length > 1 ? (
                        <Button
                          variant="outlined"
                          onClick={() => handleDeleteTrackRow(row.id)}
                          sx={{
                            color: '#FF6B6B',
                            borderColor: '#FF6B6B',
                            '&:hover': {
                              borderColor: '#FF5252',
                              backgroundColor: 'rgba(255, 107, 107, 0.1)',
                            },
                          }}
                        >
                          Delete Row
                        </Button>
                      ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                          Cannot delete the only row
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            ))}

            {/* Add Row Button */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Button
                variant="contained"
                onClick={handleAddTrackRow}
                sx={{
                  backgroundColor: '#D4AF37',
                  color: '#000000',
                  '&:hover': {
                    backgroundColor: '#B8941F',
                  },
                }}
              >
                Add Row
              </Button>
            </Box>
          </Box>
        )}

        {tabValue === 5 && (
          <Box>
            <Typography variant="h6" sx={{ color: '#D4AF37', mb: 3 }}>
              STOCKTON
            </Typography>
            <Box textAlign="center" py={4}>
              <Typography variant="h6" color="text.secondary">
                STOCKTON integration coming soon
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Manage STOCKTON-specific operations and data.
              </Typography>
            </Box>
          </Box>
        )}

        {tabValue === 6 && (
          <Box>
            <Typography variant="h6" sx={{ color: '#D4AF37', mb: 3 }}>
              Finalize
            </Typography>
            
            <Grid container spacing={3}>
              {/* Sides of Home Installed */}
              <Grid item xs={12}>
                <Card sx={{ backgroundColor: '#2C2C2C', border: '1px solid #333333' }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ color: '#D4AF37', mb: 2 }}>
                      Select the sides of the Home Installed
                    </Typography>
                    <FormGroup>
                      <Grid container spacing={2}>
                        {['Front', 'Back', 'Left', 'Right'].map((side) => (
                          <Grid item xs={6} sm={3} key={side}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={finalizeData.sidesInstalled.includes(side)}
                                  onChange={() => handleSideToggle(side)}
                                  sx={{
                                    color: '#D4AF37',
                                    '&.Mui-checked': {
                                      color: '#D4AF37',
                                    },
                                  }}
                                />
                              }
                              label={side}
                              sx={{ color: '#FFFFFF' }}
                            />
                          </Grid>
                        ))}
                      </Grid>
                    </FormGroup>
                  </CardContent>
                </Card>
              </Grid>

              {/* Light Count */}
              <Grid item xs={12} md={6}>
                <Card sx={{ backgroundColor: '#2C2C2C', border: '1px solid #333333' }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ color: '#D4AF37', mb: 2 }}>
                      Enter Light Count
                    </Typography>
                    <TextField
                      fullWidth
                      label="Number of Lights"
                      type="number"
                      value={finalizeData.lightCount}
                      onChange={(e) => handleFinalizeChange('lightCount', e.target.value)}
                      placeholder="Enter total light count"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          color: '#FFFFFF',
                          '& fieldset': {
                            borderColor: '#555555',
                          },
                          '&:hover fieldset': {
                            borderColor: '#D4AF37',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#D4AF37',
                          },
                        },
                        '& .MuiInputLabel-root': {
                          color: '#AAAAAA',
                          '&.Mui-focused': {
                            color: '#D4AF37',
                          },
                        },
                      }}
                    />
                  </CardContent>
                </Card>
              </Grid>

              {/* Controller Boxes */}
              <Grid item xs={12} md={6}>
                <Card sx={{ backgroundColor: '#2C2C2C', border: '1px solid #333333' }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ color: '#D4AF37', mb: 2 }}>
                      How Many Controller Boxes were Installed
                    </Typography>
                    <TextField
                      fullWidth
                      label="Controller Boxes"
                      type="number"
                      value={finalizeData.controllerBoxes}
                      onChange={(e) => handleFinalizeChange('controllerBoxes', e.target.value)}
                      placeholder="Enter number of controller boxes"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          color: '#FFFFFF',
                          '& fieldset': {
                            borderColor: '#555555',
                          },
                          '&:hover fieldset': {
                            borderColor: '#D4AF37',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#D4AF37',
                          },
                        },
                        '& .MuiInputLabel-root': {
                          color: '#AAAAAA',
                          '&.Mui-focused': {
                            color: '#D4AF37',
                          },
                        },
                      }}
                    />
                  </CardContent>
                </Card>
              </Grid>

              {/* Transistors */}
              <Grid item xs={12} md={6}>
                <Card sx={{ backgroundColor: '#2C2C2C', border: '1px solid #333333' }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ color: '#D4AF37', mb: 2 }}>
                      How Many Transistors are on the Home
                    </Typography>
                    <TextField
                      fullWidth
                      label="Transistors"
                      type="number"
                      value={finalizeData.transistors}
                      onChange={(e) => handleFinalizeChange('transistors', e.target.value)}
                      placeholder="Enter number of transistors"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          color: '#FFFFFF',
                          '& fieldset': {
                            borderColor: '#555555',
                          },
                          '&:hover fieldset': {
                            borderColor: '#D4AF37',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#D4AF37',
                          },
                        },
                        '& .MuiInputLabel-root': {
                          color: '#AAAAAA',
                          '&.Mui-focused': {
                            color: '#D4AF37',
                          },
                        },
                      }}
                    />
                  </CardContent>
                </Card>
              </Grid>

              {/* Yard Sign Photo */}
              <Grid item xs={12} md={6}>
                <Card sx={{ backgroundColor: '#2C2C2C', border: '1px solid #333333' }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ color: '#D4AF37', mb: 2 }}>
                      Yard Sign Photo
                    </Typography>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleYardSignPhotoUpload}
                      style={{ display: 'none' }}
                      id="yard-sign-photo-upload"
                    />
                    <Box 
                      component="label"
                      htmlFor="yard-sign-photo-upload"
                      sx={{ 
                        border: '2px dashed #555555', 
                        borderRadius: 2, 
                        p: 3, 
                        textAlign: 'center',
                        backgroundColor: '#1A1A1A',
                        minHeight: 150,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        cursor: 'pointer',
                        '&:hover': {
                          borderColor: '#D4AF37',
                          backgroundColor: 'rgba(212, 175, 55, 0.1)',
                        },
                      }}
                    >
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        📸 Upload Yard Sign Photo
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Click to upload or drag & drop
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                        Supported formats: JPG, PNG, GIF (Max 10MB)
                      </Typography>
                    </Box>
                    {finalizeData.yardSignPhoto && (
                      <Box sx={{ mt: 2, p: 2, backgroundColor: '#1A1A1A', borderRadius: 1, border: '1px solid #333333' }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          Photo Status:
                        </Typography>
                        <Chip 
                          label="Photo Uploaded" 
                          color="success" 
                          size="small"
                          sx={{ color: '#FFFFFF' }}
                        />
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              {/* WiFi Setup */}
              <Grid item xs={12} md={6}>
                <Card sx={{ backgroundColor: '#2C2C2C', border: '1px solid #333333' }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ color: '#D4AF37', mb: 2 }}>
                      Has the Customer been shown how to connect the app to WiFi
                    </Typography>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={finalizeData.wifiSetupShown}
                          onChange={(e) => handleFinalizeChange('wifiSetupShown', e.target.checked)}
                          sx={{
                            color: '#D4AF37',
                            '&.Mui-checked': {
                              color: '#D4AF37',
                            },
                          }}
                        />
                      }
                      label="WiFi setup demonstrated"
                      sx={{ color: '#FFFFFF' }}
                    />
                  </CardContent>
                </Card>
              </Grid>

              {/* App Control */}
              <Grid item xs={12} md={6}>
                <Card sx={{ backgroundColor: '#2C2C2C', border: '1px solid #333333' }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ color: '#D4AF37', mb: 2 }}>
                      Has the Customer been shown how to Control Every Aspect of the App
                    </Typography>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={finalizeData.appControlShown}
                          onChange={(e) => handleFinalizeChange('appControlShown', e.target.checked)}
                          sx={{
                            color: '#D4AF37',
                            '&.Mui-checked': {
                              color: '#D4AF37',
                            },
                          }}
                        />
                      }
                      label="App control demonstrated"
                      sx={{ color: '#FFFFFF' }}
                    />
                  </CardContent>
                </Card>
              </Grid>

              {/* Client IP Address */}
              <Grid item xs={12}>
                <Card sx={{ backgroundColor: '#2C2C2C', border: '1px solid #333333' }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ color: '#D4AF37', mb: 2 }}>
                      What is the Clients IP Address for the WLED APP
                    </Typography>
                    <TextField
                      fullWidth
                      label="Client IP Address"
                      value={finalizeData.clientIPAddress}
                      onChange={(e) => handleFinalizeChange('clientIPAddress', e.target.value)}
                      placeholder="Enter client IP address (e.g., 192.168.1.100)"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          color: '#FFFFFF',
                          '& fieldset': {
                            borderColor: '#555555',
                          },
                          '&:hover fieldset': {
                            borderColor: '#D4AF37',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#D4AF37',
                          },
                        },
                        '& .MuiInputLabel-root': {
                          color: '#AAAAAA',
                          '&.Mui-focused': {
                            color: '#D4AF37',
                          },
                        },
                      }}
                    />
                  </CardContent>
                </Card>
              </Grid>

              {/* Completion Summary */}
              <Grid item xs={12}>
                <Card sx={{ backgroundColor: '#2C2C2C', border: '1px solid #333333' }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ color: '#D4AF37', mb: 2 }}>
                      Project Completion Summary
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ p: 2, backgroundColor: '#1A1A1A', borderRadius: 1, border: '1px solid #333333' }}>
                          <Typography variant="body2" color="text.secondary">
                            Sides Installed
                          </Typography>
                          <Typography variant="h6" sx={{ color: '#FFFFFF' }}>
                            {finalizeData.sidesInstalled.length > 0 
                              ? finalizeData.sidesInstalled.join(', ') 
                              : 'None selected'
                            }
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ p: 2, backgroundColor: '#1A1A1A', borderRadius: 1, border: '1px solid #333333' }}>
                          <Typography variant="body2" color="text.secondary">
                            Total Lights
                          </Typography>
                          <Typography variant="h6" sx={{ color: '#FFFFFF' }}>
                            {finalizeData.lightCount || 'Not specified'}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ p: 2, backgroundColor: '#1A1A1A', borderRadius: 1, border: '1px solid #333333' }}>
                          <Typography variant="body2" color="text.secondary">
                            Controller Boxes
                          </Typography>
                          <Typography variant="h6" sx={{ color: '#FFFFFF' }}>
                            {finalizeData.controllerBoxes || 'Not specified'}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ p: 2, backgroundColor: '#1A1A1A', borderRadius: 1, border: '1px solid #333333' }}>
                          <Typography variant="body2" color="text.secondary">
                            Transistors
                          </Typography>
                          <Typography variant="h6" sx={{ color: '#FFFFFF' }}>
                            {finalizeData.transistors || 'Not specified'}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12}>
                        <Box sx={{ p: 2, backgroundColor: '#1A1A1A', borderRadius: 1, border: '1px solid #333333' }}>
                          <Typography variant="body2" color="text.secondary">
                            Client IP Address
                          </Typography>
                          <Typography variant="h6" sx={{ color: '#FFFFFF' }}>
                            {finalizeData.clientIPAddress || 'Not specified'}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        )}

        {tabValue === 7 && (
          <Box>
            <Typography variant="h6" sx={{ color: '#D4AF37', mb: 3 }}>
              Contact History
            </Typography>
            <Box textAlign="center" py={4}>
              <Typography variant="h6" color="text.secondary">
                Contact history coming soon
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Track all customer interactions and communications.
              </Typography>
            </Box>
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ backgroundColor: '#000000', minHeight: '100vh', p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography 
          variant="h4"
          sx={{
            color: '#D4AF37',
            fontWeight: 700,
          }}
        >
          👥 Customers
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Search customers..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="">All Statuses</MenuItem>
                  <MenuItem value="Created">Created</MenuItem>
                  <MenuItem value="Proposal">Proposal</MenuItem>
                  <MenuItem value="Signed/Paid">Signed/Paid</MenuItem>
                  <MenuItem value="Sold">Sold</MenuItem>
                  <MenuItem value="Scheduled">Scheduled</MenuItem>
                  <MenuItem value="Installed">Installed</MenuItem>
                  <MenuItem value="Canceled">Canceled</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select
                  value={typeFilter}
                  label="Type"
                  onChange={(e) => setTypeFilter(e.target.value)}
                >
                  <MenuItem value="">All Types</MenuItem>
                  <MenuItem value="residential">Residential</MenuItem>
                  <MenuItem value="commercial">Commercial</MenuItem>
                  <MenuItem value="industrial">Industrial</MenuItem>
                  <MenuItem value="municipal">Municipal</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<FilterList />}
                onClick={() => {
                  setSearchTerm('');
                  setStatusFilter('');
                  setTypeFilter('');
                }}
              >
                Clear
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Customer List Table */}
      <TableContainer component={Paper} sx={{ backgroundColor: '#1A1A1A', border: '1px solid #333333' }}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#2C2C2C' }}>
              <TableCell 
                sx={{ 
                  color: '#D4AF37', 
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  '&:hover': { backgroundColor: '#3A3A3A' }
                }}
                onClick={() => handleSort('companyName')}
              >
                <Box display="flex" alignItems="center" gap={1}>
                  Customer Name
                  {sortField === 'companyName' && (
                    sortDirection === 'asc' ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />
                  )}
                </Box>
              </TableCell>
              <TableCell sx={{ color: '#D4AF37', fontWeight: 'bold' }}>Email</TableCell>
              <TableCell sx={{ color: '#D4AF37', fontWeight: 'bold' }}>Phone</TableCell>
              <TableCell 
                sx={{ 
                  color: '#D4AF37', 
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  '&:hover': { backgroundColor: '#3A3A3A' }
                }}
                onClick={() => handleSort('status')}
              >
                <Box display="flex" alignItems="center" gap={1}>
                  Status
                  {sortField === 'status' && (
                    sortDirection === 'asc' ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />
                  )}
                </Box>
              </TableCell>
              <TableCell 
                sx={{ 
                  color: '#D4AF37', 
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  '&:hover': { backgroundColor: '#3A3A3A' }
                }}
                onClick={() => handleSort('customerType')}
              >
                <Box display="flex" alignItems="center" gap={1}>
                  Type
                  {sortField === 'customerType' && (
                    sortDirection === 'asc' ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />
                  )}
                </Box>
              </TableCell>
              <TableCell sx={{ color: '#D4AF37', fontWeight: 'bold' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {getSortedCustomers().map((customer) => (
              <TableRow 
                key={customer._id}
                sx={{ 
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: '#2C2C2C',
                  },
                  '&:nth-of-type(even)': {
                    backgroundColor: '#1A1A1A',
                  },
                }}
                onClick={() => {
                  setSelectedCustomer(customer);
                  setViewMode('detail');
                }}
              >
                <TableCell>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Avatar sx={{ width: 40, height: 40, backgroundColor: '#D4AF37', color: '#000000' }}>
                      {customer.companyName.charAt(0)}
                      {customer.companyName.split(' ')[1]?.charAt(0) || customer.companyName.charAt(1)}
                    </Avatar>
                    <Box>
                      <Typography variant="body1" sx={{ color: '#FFFFFF', fontWeight: 'medium' }}>
                        {customer.companyName}
                      </Typography>
                      {customer.assignedSalesRep && (
                        <Typography variant="caption" sx={{ color: '#AAAAAA' }}>
                          Sales Rep: {customer.assignedSalesRep.firstName} {customer.assignedSalesRep.lastName}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ color: '#AAAAAA' }}>
                    {customer.email}
                  </Typography>
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Phone fontSize="small" sx={{ color: '#D4AF37' }} />
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: '#FFFFFF',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {customer.phone}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip
                    label={customer.status}
                    color={getStatusColor(customer.status) as any}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={customer.customerType}
                    color={getTypeColor(customer.customerType) as any}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleMenuClick(e, customer);
                    }}
                    sx={{ color: '#D4AF37' }}
                  >
                    <MoreVert />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Summary Row */}
      <Box sx={{ mt: 2, p: 2, backgroundColor: '#2C2C2C', borderRadius: 1, border: '1px solid #333333' }}>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={3}>
            <Box textAlign="center">
              <Typography variant="h6" sx={{ color: '#D4AF37' }}>
                {customers.length}
              </Typography>
              <Typography variant="body2" sx={{ color: '#AAAAAA' }}>
                Total Customers
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Box textAlign="center">
              <Typography variant="h6" sx={{ color: '#D4AF37' }}>
                {customers.filter(c => c.status === 'Installed' || c.status === 'Sold').length}
              </Typography>
              <Typography variant="body2" sx={{ color: '#AAAAAA' }}>
                Active Customers
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Box>

      {customers.length === 0 && !loading && (
        <Box textAlign="center" py={4}>
          <Typography variant="h6" color="text.secondary">
            No customers found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Try adjusting your search criteria or add a new customer.
          </Typography>
        </Box>
      )}

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleView}>
          <ListItemIcon>
            <Visibility fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Details</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleEdit}>
          <ListItemIcon>
            <Edit fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDeleteClick}>
          <ListItemIcon>
            <Delete fontSize="small" />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Customer</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete {selectedCustomer?.companyName}? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Customer Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ color: '#D4AF37' }}>Edit Customer</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Company Name"
                value={editFormData.companyName}
                onChange={(e) => handleEditFormChange('companyName', e.target.value)}
                margin="normal"
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={editFormData.email}
                onChange={(e) => handleEditFormChange('email', e.target.value)}
                margin="normal"
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone"
                value={editFormData.phone}
                onChange={(e) => handleEditFormChange('phone', e.target.value)}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Customer Type</InputLabel>
                <Select
                  value={editFormData.customerType}
                  label="Customer Type"
                  onChange={(e) => handleEditFormChange('customerType', e.target.value)}
                >
                  <MenuItem value="residential">Residential</MenuItem>
                  <MenuItem value="commercial">Commercial</MenuItem>
                  <MenuItem value="industrial">Industrial</MenuItem>
                  <MenuItem value="municipal">Municipal</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Status</InputLabel>
                <Select
                  value={editFormData.status}
                  label="Status"
                  onChange={(e) => handleEditFormChange('status', e.target.value)}
                >
                  <MenuItem value="Created">Created</MenuItem>
                  <MenuItem value="Proposal">Proposal</MenuItem>
                  <MenuItem value="Signed/Paid">Signed/Paid</MenuItem>
                  <MenuItem value="Sold">Sold</MenuItem>
                  <MenuItem value="Scheduled">Scheduled</MenuItem>
                  <MenuItem value="Installed">Installed</MenuItem>
                  <MenuItem value="Canceled">Canceled</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Street Address"
                value={editFormData.address.street}
                onChange={(e) => handleEditFormChange('address.street', e.target.value)}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="City"
                value={editFormData.address.city}
                onChange={(e) => handleEditFormChange('address.city', e.target.value)}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="State"
                value={editFormData.address.state}
                onChange={(e) => handleEditFormChange('address.state', e.target.value)}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="ZIP Code"
                value={editFormData.address.zipCode}
                onChange={(e) => handleEditFormChange('address.zipCode', e.target.value)}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                value={editFormData.notes}
                onChange={(e) => handleEditFormChange('notes', e.target.value)}
                margin="normal"
                multiline
                rows={3}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)} sx={{ color: '#FFFFFF' }}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveCustomer}
            variant="contained"
            disabled={loading}
            sx={{ backgroundColor: '#D4AF37', color: '#000000', '&:hover': { backgroundColor: '#B8941F' } }}
          >
            {loading ? <CircularProgress size={24} /> : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Customers;
